// ============================================
// Sidebar Navigation Component
// Mobile-first responsive design
// ============================================

import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  Building2,
  ShoppingCart,
  FileBarChart,
  CalendarRange,
  Menu,
  X,
} from 'lucide-react';
import { useEffect } from 'react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const navItems: NavItem[] = [
  {
    path: '/',
    label: 'Overview',
    icon: <LayoutDashboard className="w-5 h-5" />,
    color: 'blue',
  },
  {
    path: '/const-funnel',
    label: 'CONST Funnel',
    icon: <Building2 className="w-5 h-5" />,
    color: 'blue',
  },
  {
    path: '/ecom-funnel',
    label: 'ECOM Funnel',
    icon: <ShoppingCart className="w-5 h-5" />,
    color: 'purple',
  },
  {
    path: '/ecom-monthly-kpi',
    label: 'ECOM Report (Feb)',
    icon: <CalendarRange className="w-5 h-5" />,
    color: 'purple',
  },
  {
    path: '/monthly-report',
    label: 'Monthly Report',
    icon: <FileBarChart className="w-5 h-5" />,
    color: 'green',
  },
];

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

export function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }: SidebarProps) {
  // Close mobile menu on route change
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setMobileOpen]);

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 h-screen bg-white border-r border-gray-200
          flex flex-col transition-all duration-300 ease-in-out z-50
          ${collapsed ? 'w-[72px]' : 'w-64'}
          ${mobileOpen ? 'left-0' : '-left-64 lg:left-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          <div className={`flex items-center gap-3 ${collapsed ? 'justify-center w-full' : ''}`}>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
              <span className="text-white font-bold text-sm">ST</span>
            </div>
            {!collapsed && (
              <div className="flex flex-col">
                <span className="font-bold text-gray-900">Space T</span>
                <span className="text-xs text-gray-500">Marketing Dashboard</span>
              </div>
            )}
          </div>
          {/* Desktop collapse button */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`hidden lg:flex p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer ${collapsed ? 'absolute -right-3 top-5 bg-white border border-gray-200 shadow-sm' : ''}`}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
          {/* Mobile close button */}
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 overflow-y-auto">
          {!collapsed && (
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-3">
              Dashboard
            </p>
          )}
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 cursor-pointer group ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    } ${collapsed ? 'justify-center' : ''}`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={isActive ? 'text-white' : 'text-gray-500 group-hover:text-gray-700'}>
                        {item.icon}
                      </span>
                      {!collapsed && (
                        <span className="font-medium">{item.label}</span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        {!collapsed && (
          <div className="p-4 border-t border-gray-200">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Marketing Platform</p>
              <p className="text-sm font-semibold text-gray-900">Space T 2026</p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

// Mobile Menu Button Component
export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="lg:hidden fixed top-4 left-4 z-30 p-2.5 bg-white rounded-xl shadow-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
      aria-label="Open menu"
    >
      <Menu className="w-5 h-5" />
    </button>
  );
}
